import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { browserTimezone } from '../../lib/datetime.js'
import { api, type CourseCard, type HomeworkRow } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Bar, ButtonLink, ErrorLine, More, RowText, Rows, SearchBox, SectionHead, Skeleton, Title } from '../../classroom/ui.js'
import { titleFor } from './CoursePage.js'
import { studentCopy } from './copy.js'
import { shellCopy } from './copyShell.js'

/** About 25 seconds a flashcard, rounded up — honest enough to plan a coffee break by. */
const reviewMinutes = (cards: number) => Math.max(1, Math.ceil((cards * 25) / 60))

/** "czwartek, 23 października" → "Czwartek, 23 października". */
export function capitalise(s: string): string {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1)
}

/**
 * Homework worth pointing at today: one already started beats a fresh one,
 * and the earliest lesson first — the order a student would pick them up in.
 */
export function nextHomework(rows: HomeworkRow[] | undefined): HomeworkRow | null {
  const open = (rows ?? []).filter((h) => h.done < h.parts)
  const doing = open.filter((h) => h.done > 0)
  const pool = doing.length > 0 ? doing : open
  return pool.sort((a, b) => a.number - b.number)[0] ?? null
}

/** The course the hero card continues: one already under way, else the first with a lesson left. */
function currentCourse(courses: CourseCard[] | undefined): CourseCard | null {
  const open = (courses ?? []).filter((c) => c.next)
  return open.find((c) => c.completed > 0 || c.next?.started) ?? open[0] ?? null
}

/**
 * Start. One hero card (the lesson to continue), then what is due today, then
 * the courses. Everything else has its own screen in the menu.
 */
export function StudentHomePage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  const h = s.home
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const me = useQuery({ queryKey: ['student', 'me'], queryFn: api.student.me })
  const courses = useQuery({ queryKey: ['course', 'mine'], queryFn: api.course.mine })
  const homework = useQuery({ queryKey: ['course', 'homework'], queryFn: api.course.homework })
  const review = useQuery({ queryKey: ['review', 'status'], queryFn: api.review.status })

  const course = currentCourse(courses.data)
  const lesson = course?.next ?? null
  const due = review.data?.due ?? 0
  const hw = nextHomework(homework.data)
  const upcoming = me.data?.lessons.upcoming[0] ?? null
  /*
   * The student's own zone, from their device — as on the booking page, where
   * they picked the time. profile.timezone is never set (it is always
   * Europe/Warsaw), so a student in Kyiv who booked 18:00 read „17:00”.
   */
  const tz = browserTimezone()

  const summary = [lesson ? h.summaryLesson(lesson.number) : null, due > 0 ? h.summaryReviews(due) : null].filter(
    (x): x is string => Boolean(x),
  )

  const search = () => {
    const q = query.trim()
    navigate(q ? `/panel/slownik?q=${encodeURIComponent(q)}` : '/panel/slownik')
  }

  return (
    <Column
      top={
        <>
          <TopTitle>{s.nav.home}</TopTitle>
          <SearchBox
            className="ml-auto"
            value={query}
            onChange={setQuery}
            onSubmit={search}
            placeholder={s.search.placeholder}
            label={s.search.label}
          />
        </>
      }
      bottom={
        <>
          {summary.length > 0 && <span className="text-class-gloss text-class-ink-soft">{h.summary(summary)}</span>}
          {course && <More to={`/panel/kurs/${course.slug}`} className="ml-auto">{h.wholeCourse}</More>}
        </>
      }
    >
      <Main>
        {me.isPending || courses.isPending ? (
          <Skeleton lines={4} />
        ) : me.isError || courses.isError ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : (
          <>
            <Title sub={me.data.streak > 0 ? h.streak(me.data.streak) : undefined}>
              {h.greeting(me.data.profile.name.split(/\s+/)[0] ?? me.data.profile.name)}
            </Title>

            {/* ── the lesson to continue ─────────────────────────────── */}
            {course && lesson && (
              <section className="flex flex-col gap-5 rounded-class-lg bg-class-sky-200 p-[22px] desk:p-8">
                <div className="flex flex-col gap-1">
                  <span className="text-class-gloss font-semibold text-class-sky-700">
                    {h.heroLine(course.title, lesson.number, course.lessons)}
                  </span>
                  <p lang="pl" className="m-0 font-class-display text-class-heading leading-tight">
                    {lesson.title}
                  </p>
                  {locale !== 'pl' && titleFor(lesson, locale) !== lesson.title && (
                    <p className="m-0 text-class-ink-soft">{titleFor(lesson, locale)}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                  <ButtonLink to={`/panel/kurs/${course.slug}/${lesson.slug}`} className="min-h-12 max-desk:w-full">
                    {lesson.started ? h.continueLesson : h.startLesson}
                  </ButtonLink>
                  {lesson.gradable > 0 && (
                    <span className="flex min-w-40 flex-1 items-center gap-3">
                      <Bar track="white" value={(lesson.solved / lesson.gradable) * 100} className="flex-1" />
                      <span className="text-class-gloss text-class-ink-soft">
                        {Math.round((lesson.solved / lesson.gradable) * 100)}%
                      </span>
                    </span>
                  )}
                </div>
              </section>
            )}
            {courses.data.length > 0 && !course && (
              <section className="rounded-class-lg bg-class-sky-200 p-[22px] desk:p-8">
                <p className="m-0 font-class-display text-class-heading">{h.allDone}</p>
              </section>
            )}

            {/* ── today ───────────────────────────────────────────────── */}
            <section className="flex flex-col">
              <SectionHead>{h.today}</SectionHead>
              {due === 0 && !hw && !upcoming ? (
                <p className="m-0 text-class-ink-soft">{h.nothingToday}</p>
              ) : (
                <Rows>
                  {due > 0 && (
                    <TodayRow
                      to="/panel/powtorki"
                      label={h.review}
                      title={h.words(due)}
                      meta={h.minutes(reviewMinutes(due))}
                      action={h.repeat}
                    />
                  )}
                  {hw && (
                    <TodayRow
                      to={`/panel/kurs/${hw.courseSlug}/${hw.lessonSlug}/zadanie`}
                      label={h.homework}
                      title={`${t.course.lesson} ${hw.number} · ${hw.title}`}
                      meta={h.homeworkMeta(hw.parts)}
                      action={h.open}
                    />
                  )}
                  {upcoming && (
                    <TodayRow
                      to="/panel/lekcje"
                      label={h.lessonWithDiana}
                      title={capitalise(DateTime.fromISO(upcoming.startsAt).setZone(tz).setLocale(locale).toFormat('cccc, d MMMM'))}
                      meta={`${DateTime.fromISO(upcoming.startsAt).setZone(tz).toFormat('HH:mm')}–${DateTime.fromISO(upcoming.endsAt).setZone(tz).toFormat('HH:mm')}`}
                      action={h.details}
                    />
                  )}
                </Rows>
              )}
            </section>

            {/* ── courses ─────────────────────────────────────────────── */}
            <section className="flex flex-col">
              <SectionHead>{h.yourCourses}</SectionHead>
              {courses.data.length === 0 ? (
                <div>
                  <p className="m-0 font-semibold">{h.noCourses}</p>
                  <p className="m-0 text-class-gloss text-class-ink-faint">{h.noCoursesHint}</p>
                </div>
              ) : (
                <Rows>
                  {courses.data.map((c) => (
                    <Link
                      key={c.id}
                      to={`/panel/kurs/${c.slug}`}
                      className="flex min-h-[72px] items-center gap-6 px-1 py-3 !text-class-ink hover:bg-class-field-soft/50"
                    >
                      <RowText
                        title={c.title}
                        meta={
                          (c.completed > 0 ? h.ofLessons(c.completed, c.lessons) : h.notStarted(c.lessons)) +
                          (c.published ? '' : ' · szkic')
                        }
                      />
                      <Bar value={c.lessons ? (c.completed / c.lessons) * 100 : 0} className="w-24 shrink-0 desk:w-[140px]" />
                    </Link>
                  ))}
                </Rows>
              )}
            </section>
          </>
        )}
      </Main>
    </Column>
  )
}

function TodayRow({ to, label, title, meta, action }: { to: string; label: string; title: string; meta: string; action: string }) {
  return (
    <Link to={to} className="flex min-h-[72px] items-center gap-4 px-1 py-3 !text-class-ink hover:bg-class-field-soft/50 desk:gap-6">
      <span className="hidden w-[140px] shrink-0 text-class-gloss text-class-ink-faint desk:block">{label}</span>
      <RowText title={title} meta={<><span className="desk:hidden">{label} · </span>{meta}</>} />
      <span className="whitespace-nowrap font-semibold text-class-sky-700">{action} ›</span>
    </Link>
  )
}
